/**
 * GameContext / GameProvider — assembler around three SRP-focused units
 * (PR-V3.E split):
 *
 *   - normalizeStart  : pure conversion of `gameData.start` -> Unix seconds.
 *   - useGameSync     : token -> findGame -> Realtime + polling + cleanup.
 *   - useGameTimer    : client setTimeout + isTicking / lastTickedCurrent
 *                       guards firing fetchTick(token).
 *
 * The Provider keeps the same public shape as before
 * (see `./types.ts -> GameContextValue`) so existing consumers
 * (`GameView`, `NewGame`) are unaffected.
 */

import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from "react"
import { WAITING, GameStatus } from "../../utils/gameStates"
import { getClient } from "../../lib/supabase/get-client"
import type { SupabaseClient } from "@supabase/supabase-js"
import { normalizeStart } from "./normalize"
import { useGameSync } from "./useGameSync"
import { useGameTimer } from "./useGameTimer"
import { useGamePlayers } from "./useGamePlayers"
import { useGameAnswers } from "./useGameAnswers"
import { usePlayerNames } from "./usePlayerNames"
import type { GameContextValue } from "./types"

const debug =
  process.env.GATSBY_DEBUG_MODE === "true" ||
  process.env.GATSBY_DEBUG_MODE === "1"

export const GameContext = createContext<GameContextValue>({
  token: null,
  setToken: () => {},
  gameRef: null,
  setGameRef: () => {},
  status: WAITING,
  current: 0,
  start: 0,
  stock: [],
  players: [],
  answers: [],
  pairingCode: null,
  playerNames: {},
  refetchGame: async () => {},
})

interface GameProviderProps {
  children: ReactNode
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const supabase = useMemo<SupabaseClient | null>(() => {
    if (typeof window === "undefined") {
      return null
    }
    return getClient(process.env.GATSBY_SUPA_KEY)
  }, [])
  const [token, setToken] = useState<string | null>(null)
  const { gameRef, setGameRef, gameData, stock, refetchGame } = useGameSync(
    token,
    supabase
  )
  const [status, setStatus] = useState<GameStatus | null>(null)
  const [current, setCurrent] = useState<number>(0)
  const [start, setStart] = useState<number>(0)
  const [pairingCode, setPairingCode] = useState<string | null>(null)

  // Per-game player + answer lists now come from their own Realtime-backed
  // hooks on the normalized tables (audit M8 / B7). useGameSync still
  // delivers the game-row fields (status / current / start / pairing_code).
  const players = useGamePlayers(gameRef, supabase)
  const answers = useGameAnswers(gameRef, supabase)

  useEffect(() => {
    setStatus(gameData?.status || null)
    setCurrent(gameData?.current || 0)
    setPairingCode(gameData?.pairing_code || null)
    const normalizedStart = normalizeStart(gameData?.start)
    if (normalizedStart !== null) {
      setStart(normalizedStart)
    }
  }, [gameData])

  useGameTimer({ token, status, start, current, refetchGame })

  const playerNames = usePlayerNames(players, supabase)

  if (debug) {
    console.log("[GameContext] State:", { status, current, gameRef })
  }

  const value = useMemo<GameContextValue>(
    () => ({
      token,
      setToken,
      gameRef,
      setGameRef,
      status,
      start,
      current,
      stock,
      players,
      answers,
      pairingCode,
      playerNames,
      refetchGame,
    }),
    [
      token,
      gameRef,
      setGameRef,
      status,
      start,
      current,
      stock,
      players,
      answers,
      pairingCode,
      playerNames,
      refetchGame,
    ]
  )

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>
}

export default GameContext
